import { msg } from '../../../../i18n/msg';

/**
 * Real-time brawl constants mirrored from the server contract
 * (.claude/lair-feature/SPEC_V141_GAMES.md §1–§3). The server is authoritative —
 * these are only used for rendering and own-fighter movement prediction.
 */

export const WORLD_W = 1000;
export const TICK_MS = 50;
export const INTERP_DELAY_MS = 100;
export const HITBOX = 25;

// ── 2D world (spec §11.1): x → right, y ↑ UP from the floor (y = 0), fighter y = feet ──
export const ARENA_W = 600; // PvP portrait arena
export const ARENA_H = 900;
export const BRAWLER_W = 1000; // Brawler panel (floor only)
export const BRAWLER_H = 560;
export const GRAVITY = 2400; // u/s²
export const JUMP_VY = 1050; // u/s; semi-implicit Euler at 50 ms ticks → apex ≈ 204 (170 ledges reachable)
export const PHYS_STEP_SEC = TICK_MS / 1000; // the server integrates per tick: vy −= g·dt, then y += vy·dt
export const JUMP_BUFFER_MS = 250; // a jump pressed in the air fires on landing within this window
export const FIGHTER_W = 50;
export const FIGHTER_H = 110;
export const SNAP_DIST = 120;

/** Default PvP platforms (the server sends `match.platforms`; this is only a fallback). */
export const PVP_PLATFORMS = [
  { x1: 40, x2: 230, y: 170 },
  { x1: 370, x2: 560, y: 170 },
  { x1: 200, x2: 400, y: 340 },
  { x1: 30, x2: 200, y: 510 },
  { x1: 400, x2: 570, y: 510 },
  { x1: 220, x2: 380, y: 680 },
];

/** match → {w, h, platforms}. PvP without `h` → portrait arena, PvE → landscape panel. */
export function worldOf(match) {
  const pve = match?.mode === 'PVE';
  const w = Number(match?.w) || (pve ? BRAWLER_W : ARENA_W);
  const h = Number(match?.h) || (pve ? BRAWLER_H : (w === ARENA_W ? ARENA_H : BRAWLER_H));
  const raw = Array.isArray(match?.platforms) ? match.platforms : (!pve && w === ARENA_W ? PVP_PLATFORMS : []);
  const platforms = raw
    .map((p) => ({ x1: Number(p.x1), x2: Number(p.x2), y: Number(p.y) }))
    .filter((p) => Number.isFinite(p.x1) && Number.isFinite(p.x2) && p.y > 0);
  return { w, h, platforms };
}
export const MAX_GUARD = 40;
export const MAX_MANA = 100;

// Fighter states (`st` in a snapshot)
export const ST = {
  IDLE: 0,
  WALK: 1,
  ATK_WINDUP: 2,
  ATK_ACTIVE: 3,
  ATK_RECOVER: 4,
  BLOCK: 5,
  CAST: 6,
  STUN: 7,
  DEAD: 8,
  BUFF_ANIM: 9,
};

export const ST_NAMES = ['idle', 'walk', 'windup', 'active', 'recover', 'block', 'cast', 'stun', 'dead', 'buff'];

export const stateName = (st) => ST_NAMES[st] || 'idle';

/** States in which the server lets a fighter walk (speed 0 otherwise). Full air control. */
export const canMoveInState = (st) => st === ST.IDLE || st === ST.WALK;

/**
 * States from which a jump / drop is accepted (only from a support — checked separately).
 * A jump during attack recovery cancels the recovery (jump-cancel).
 */
export const canJumpInState = (st) => st === ST.IDLE || st === ST.WALK || st === ST.ATK_RECOVER;

/**
 * Walking speed (spec §1): 260 · (0.9 + 0.3·sA) u/s, sA = AGI / (STR + AGI + INT).
 * Balanced hero → 260. SLOW (Web Shot) halves it.
 */
export function fighterSpeed({ str, agi, int } = {}, slowed = false) {
  const s = Number(str) || 0;
  const a = Number(agi) || 0;
  const i = Number(int) || 0;
  const p = s + a + i;
  const sA = p > 0 ? a / p : 1 / 3;
  const base = 260 * (0.9 + 0.3 * sA);
  return slowed ? base * 0.5 : base;
}

/**
 * Spell visuals. Projectile `k` / cast `k` / fighter `spell` strings are matched loosely
 * (server key naming is not fixed by the spec: "BATARANG", "Batarang", "SHIELD_THROW"…),
 * falling back to the hero class (`character`).
 */
export const SPELLS = {
  batarang: { name: msg('spell.batarang', 'Batarang'), icon: '🦇', color: '#ffd84a', word: 'ZING!' },
  shield: { name: msg('spell.shield', 'Shield Throw'), icon: '🛡', color: '#5aa0ff', word: 'CLANG!' },
  web: { name: msg('spell.web', 'Web Shot'), icon: '🕸', color: '#f2f2f2', word: 'THWIP!' },
  shell: { name: msg('spell.shell', 'Shell Up'), icon: '🐢', color: '#5ee67a', word: 'SHELL!' },
  berserk: { name: msg('spell.berserk', 'Berserk'), icon: '🩸', color: '#ff3d3d', word: 'RAAAH!' },
  blaster: { name: msg('spell.blaster', 'Blaster Burst'), icon: '🔫', color: '#ff4d4d', word: 'PEW!' },
  dive: { name: msg('spell.dive', 'Dive Strike'), icon: '🦅', color: '#ffb547', word: 'SWOOP!' },
  banana: { name: msg('spell.banana', 'Banana Trap'), icon: '🍌', color: '#ffe14a', word: 'SLIP!' },
  orb: { name: msg('spell.orb', 'Magic'), icon: '✨', color: '#b36bff', word: 'ZAP!' },
};

const SPELL_PATTERNS = [
  [/BATARANG|BATMAN|\bBAT\b/, 'batarang'],
  [/SHIELD|CAPTAIN/, 'shield'],
  [/WEB|SPIDER/, 'web'],
  [/SHELL|TURTLE/, 'shell'],
  [/BERSERK|WOLVERINE|CLAW/, 'berserk'],
  [/BLAST|BOLT|STORMTROOPER|TROOPER|LASER/, 'blaster'],
  [/DIVE|EAGLE/, 'dive'],
  [/BANANA|MONKEY|TRAP/, 'banana'],
];

/** 'Shield Throw' | 'SHIELD' | 'CAPTAIN' → 'shield'; unknown → 'orb'. */
export function spellKey(...candidates) {
  for (const c of candidates) {
    if (!c) continue;
    const s = String(c).toUpperCase().replace(/[_-]+/g, ' ');
    const hit = SPELL_PATTERNS.find(([re]) => re.test(s));
    if (hit) return hit[1];
  }
  return 'orb';
}

export const spellMeta = (...candidates) => SPELLS[spellKey(...candidates)];

/** Colour schemes of the fallback (no part images) figure per hero class / enemy kind. */
export const PALETTES = {
  BATMAN: { skin: '#2b2b33', suit: '#3a3a46', accent: '#ffd84a', cape: '#16161c' },
  CAPTAIN: { skin: '#f1c6a0', suit: '#2c5ad6', accent: '#e63946', cape: null },
  SPIDER: { skin: '#d6283c', suit: '#d6283c', accent: '#2457c5', cape: null },
  TURTLE: { skin: '#58b347', suit: '#3e8a33', accent: '#c58b3a', cape: null },
  WOLVERINE: { skin: '#f1c6a0', suit: '#f2c230', accent: '#2457c5', cape: null },
  STORMTROOPER: { skin: '#f4f4f4', suit: '#e9e9ee', accent: '#1b1b1f', cape: null },
  EAGLE: { skin: '#f6f1e6', suit: '#7a4b2a', accent: '#ffb547', cape: '#5a361d' },
  MONKEY: { skin: '#c99568', suit: '#7b4a2b', accent: '#ffe14a', cape: null },
  thug: { skin: '#b58867', suit: '#5b5470', accent: '#ff588b', cape: null },
  brute: { skin: '#8f6a52', suit: '#6b2f2f', accent: '#ffb547', cape: null },
  boss: { skin: '#5a3d6b', suit: '#2a1636', accent: '#ff3d3d', cape: '#7a1020' },
  hero: { skin: '#f1c6a0', suit: '#693de9', accent: '#cfff1f', cape: null },
};

export function paletteFor(meta) {
  if (!meta) return PALETTES.hero;
  if (meta.kind && meta.kind !== 'hero' && PALETTES[meta.kind]) return PALETTES[meta.kind];
  const c = String(meta.character || '').toUpperCase();
  return PALETTES[c] || PALETTES.hero;
}

/** Figure scale per enemy kind (boss is big). */
export const KIND_SCALE = { hero: 1, thug: 0.95, brute: 1.2, boss: 1.38 };
